/**
 * priceBasis.js — one definition of "what unit is this price quoted in".
 *
 * Stored costs in this app are per the item's STOCK unit: InventoryBatches.unit_cost on
 * a kg item is a per-kg number, on a g item a per-gram one. Every screen that wants to
 * show or reuse that price wants it per some OTHER unit, and each conversion done at the
 * call site is another chance to pair a per-kg figure with a 'g' label — a silent 1000x
 * error in a number the user is about to accept as a cost.
 *
 * So conversions go through the base unit (g / ml / pcs), once, here.
 */

import UnitMappingService from '../services/unitMappingService';

/**
 * Cost basis for an inventory item, restated per BASE unit (g / ml / pcs).
 *
 * @param {{latestUnitCost?: number, stockUnitSymbol?: string, rawUnit?: string, unit?: string}} item
 *   `latestUnitCost` is the item's latest purchase price, quoted per `stockUnitSymbol`.
 * @returns {{costPerBase: number, baseUnit: string, unit: string}|null} null when the item
 *   has no usable price — never a zero, which would read as a real ₹0 price.
 */
export const getItemCostBasisPerBaseUnit = (item) => {
  const cost = Number(item?.latestUnitCost);
  if (!Number.isFinite(cost) || cost <= 0) return null;

  const stockUnit = item?.stockUnitSymbol || item?.rawUnit || item?.unit;
  const baseUnitsPerStockUnit = UnitMappingService.factorToBase(stockUnit);
  if (!(baseUnitsPerStockUnit > 0)) return null;

  return {
    costPerBase: cost / baseUnitsPerStockUnit,
    baseUnit: UnitMappingService.baseUnitOf(stockUnit),
    unit: stockUnit
  };
};

/**
 * Restate a per-base-unit cost as a price per `targetUnit`.
 *
 * Returns null across unit families (a ₹/g basis cannot price a millilitre) rather than
 * passing the number through unscaled — silently pricing 1 ml at the per-gram rate is
 * exactly the class of error that makes a suggested price untrustworthy.
 *
 * @returns {number|null}
 */
export const pricePerUnitFromBaseCost = (costPerBase, baseUnit, targetUnit) => {
  const cost = Number(costPerBase);
  if (!Number.isFinite(cost) || cost <= 0) return null;
  if (!targetUnit) return null;

  const targetBase = UnitMappingService.baseUnitOf(targetUnit);
  if (baseUnit && targetBase && targetBase !== baseUnit) return null;

  const factor = UnitMappingService.factorToBase(targetUnit);
  if (!(factor > 0)) return null;

  const price = cost * factor;
  return Number.isFinite(price) && price > 0 ? price : null;
};

/** Price string for an input: enough decimals that a per-gram price survives. */
export const formatAutoPriceValue = (price) => String(Number(Number(price).toFixed(4)));
